import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  logout: () => api.post('/api/auth/logout'),
  getCurrentUser: () => api.get('/api/auth/me'),
};

// Carousel API
export const carouselAPI = {
  getAll: () => api.get('/api/carousel'),
  getAllAdmin: () => api.get('/api/carousel/all'),
  create: (data) => api.post('/api/carousel', data),
  update: (id, data) => api.put(`/api/carousel/${id}`, data),
  delete: (id) => api.delete(`/api/carousel/${id}`),
};

// Packages API
export const packagesAPI = {
  getAll: () => api.get('/api/packages'),
  getAllAdmin: () => api.get('/api/packages/all'),
  create: (data) => api.post('/api/packages', data),
  update: (id, data) => api.put(`/api/packages/${id}`, data),
  delete: (id) => api.delete(`/api/packages/${id}`),
};

// Blog API
export const blogAPI = {
  getAll: () => api.get('/api/blog'),
  getAllAdmin: () => api.get('/api/blog/all'),
  getOne: (id) => api.get(`/api/blog/${id}`),
  create: (data) => api.post('/api/blog', data),
  update: (id, data) => api.put(`/api/blog/${id}`, data),
  delete: (id) => api.delete(`/api/blog/${id}`),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (email) => api.post('/api/newsletter/subscribe', { email }),
  getSubscribers: () => api.get('/api/newsletter'),
  deleteSubscriber: (id) => api.delete(`/api/newsletter/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/api/contact/submit', data),
  getAll: () => api.get('/api/contact'),
  updateStatus: (id, status) => api.put(`/api/contact/${id}/status`, null, { params: { status } }),
  delete: (id) => api.delete(`/api/contact/${id}`),
};

// Neighborhoods/Gallery API
export const neighborhoodsAPI = {
  getAll: () => api.get('/api/neighborhoods'),
  getAllAdmin: () => api.get('/api/neighborhoods/admin'),
  getOne: (id) => api.get(`/api/neighborhoods/${id}`),
  create: (data) => api.post('/api/neighborhoods', data),
  update: (id, data) => api.put(`/api/neighborhoods/${id}`, data),
  delete: (id) => api.delete(`/api/neighborhoods/${id}`),
};

// Videos API
export const videosAPI = {
  getAll: () => api.get('/api/videos'),
  getAllAdmin: () => api.get('/api/videos/admin'),
  getOne: (id) => api.get(`/api/videos/${id}`),
  create: (data) => api.post('/api/videos', data),
  update: (id, data) => api.put(`/api/videos/${id}`, data),
  delete: (id) => api.delete(`/api/videos/${id}`),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return api.post('/api/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Trails API
export const trailsAPI = {
  getAll: () => api.get('/api/trails'),
  getAllAdmin: () => api.get('/api/trails/admin'),
  getOne: (id) => api.get(`/api/trails/${id}`),
  create: (data) => api.post('/api/trails', data),
  update: (id, data) => api.put(`/api/trails/${id}`, data),
  delete: (id) => api.delete(`/api/trails/${id}`),
  seed: () => api.post('/api/trails/seed'),
};

// Page Content API
export const pageContentAPI = {
  getAll: (page, section) => {
    let url = '/api/page-content';
    const params = [];
    if (page) params.push(`page=${page}`);
    if (section) params.push(`section=${section}`);
    if (params.length) url += '?' + params.join('&');
    return api.get(url);
  },
  getAllAdmin: (page) => {
    let url = '/api/page-content/admin';
    if (page) url += `?page=${page}`;
    return api.get(url);
  },
  getPages: () => api.get('/api/page-content/pages'),
  getOne: (id) => api.get(`/api/page-content/${id}`),
  create: (data) => api.post('/api/page-content', data),
  update: (id, data) => api.put(`/api/page-content/${id}`, data),
  delete: (id) => api.delete(`/api/page-content/${id}`),
  seed: () => api.post('/api/page-content/seed'),
};

export default api;
